/**
 * Pure helpers for the EHR monitoring views: relative-time parts, the status
 * filter list, the status a connection is filtered, sorted and counted by,
 * direction glyphs, the organization subline and the "Resources Enabled" summary.
 * No React or intl here; the components format what these return.
 */
import type {
  EhrSyncConnection,
  EhrSyncDirection,
  EhrSyncResource,
  EhrSyncStatus,
} from '@models/ehr-sync';

/**
 * The status a connection is filtered, sorted and counted by. A zone missing
 * or renamed in QIE counts as failed; a provisioning or disabled connection as
 * pending. Otherwise it is the runtime status. The table still shows the
 * registry chip; this only decides which bucket the row falls in.
 *
 * @param connection The connection's runtime status and registry state
 * @returns The status used by the filter, the sort and the summary cards
 */
export function effectiveStatus(
  connection: Pick<EhrSyncConnection, 'status' | 'registryState'>
): EhrSyncStatus {
  switch (connection.registryState) {
    case 'zoneNotFound':
    case 'zoneNameMismatch':
      return 'failed';
    case 'provisioning':
    case 'disabled':
      return 'pending';
    default:
      return connection.status;
  }
}

/** Compact glyph for a sync direction. Decorative: pair it with a text label. */
export const DIRECTION_GLYPH: Record<EhrSyncDirection, string> = {
  inbound: '←',
  outbound: '→',
  bidirectional: '⇄',
};

/** Options for the status filter, in display order. */
export const STATUS_FILTERS: (EhrSyncStatus | 'all')[] = [
  'all',
  'connected',
  'degraded',
  'failed',
  'pending',
];

/** A relative-time value ready for `Intl.RelativeTimeFormat`. */
export interface RelativeTimeParts {
  /** Non-negative elapsed amount in `unit`. */
  value: number;
  unit: 'second' | 'minute' | 'hour' | 'day';
}

/**
 * Splits an ISO timestamp into a coarse elapsed value and unit.
 * @param iso The timestamp; null, undefined and unparsable values give null
 * @param now Reference time in ms, defaulting to the current time
 * @returns The elapsed parts, or null when there is no usable timestamp
 */
export function relativeTimeParts(
  iso?: string | null,
  now = Date.now()
): RelativeTimeParts | null {
  if (!iso) {
    return null;
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return null;
  }
  const minutes = Math.round((now - then) / 60000);
  if (minutes < 1) {
    return { value: 0, unit: 'second' };
  }
  if (minutes < 60) {
    return { value: minutes, unit: 'minute' };
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return { value: hours, unit: 'hour' };
  }
  return { value: Math.round(hours / 24), unit: 'day' };
}

/** The visible part of a "Resources Enabled" cell. */
export interface RunningResourceSummary {
  /** Up to `max` running resources, in order. */
  shown: EhrSyncResource[];
  /** Running resources beyond `max`. */
  overflow: number;
}

/**
 * Picks the running channels to show in the list's "Resources Enabled" cell.
 * @param resources All of a connection's resources
 * @param max How many to show before collapsing the rest into a count
 * @returns The resources to render and how many were left out
 */
export function summarizeRunningResources(
  resources: EhrSyncResource[],
  max = 4
): RunningResourceSummary {
  const running = resources.filter((resource) => resource.state === 'running');
  return {
    shown: running.slice(0, max),
    overflow: Math.max(0, running.length - max),
  };
}

/**
 * Joins the caption shown under an organization name, dropping blanks and any
 * part that repeats the name. The QIE zone description usually is the org's
 * display name (the backend falls back to it when the zone is not a known
 * account), so repeating it under the name adds nothing.
 * @param name The organization name rendered above the caption
 * @param parts Caption candidates in display order, e.g. environment, description
 * @returns The joined caption, or an empty string when nothing is left
 */
export function organizationSubline(
  name: string,
  parts: (string | null | undefined)[]
): string {
  const normalizedName = name.trim().toLowerCase();
  return parts
    .map((part) => part?.trim() ?? '')
    .filter((part) => part && part.toLowerCase() !== normalizedName)
    .join(' · ');
}
